import React from 'react'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Link,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { useTranslation } from 'react-i18next'

import type { InternalCustomersOutput } from '@core/api/generated'
import { INTERNAL_CUSTOMERS_ROUTE } from '@core/constants/routes'
import { formatTimeAgo } from '@core/utils/date'

import { useListCustomers, useSwitchCustomer } from './admin-api'
import { AdminSettings } from './admin-settings-state'

type AdminSwitchCustomerContentProps = {
  adminSettings: AdminSettings
}

const AdminSwitchCustomerContent = (props: AdminSwitchCustomerContentProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.switch_customer' })
  const { customer, handleClose } = props.adminSettings
  const [selectedCustomerId, setSelectedCustomerId] = React.useState(customer?.id || '')
  const enableSwitch = selectedCustomerId && selectedCustomerId !== customer!.id
  const { data: customers } = useListCustomers()
  const switchCustomer = useSwitchCustomer()

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault()
        switchCustomer.mutate(selectedCustomerId)
      }}
    >
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 4 }}>
          {t('deprecation_warning')}{' '}
          <Link href={INTERNAL_CUSTOMERS_ROUTE} underline="hover">
            {t('customers_list_page')}
          </Link>
          {t('deprecation_suffix')}
        </Alert>
        <Autocomplete
          options={customers}
          sx={{ pb: 3 }}
          onChange={(e, value) => setSelectedCustomerId(value?.id || '')}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label={customers ? t('select_customer') : t('loading')}
              fullWidth
              helperText={t('reload_warning')}
              disabled={!customers}
            />
          )}
          value={customers?.find((item) => item.id === selectedCustomerId) || null}
          getOptionLabel={(option) => option.name}
          renderOption={(renderProps, option: InternalCustomersOutput) => (
            <ListItem {...renderProps} key={option.id} sx={{ padding: '0px !important' }}>
              <ListItemButton role={undefined} dense>
                <ListItemText
                  id={option.id}
                  color="text.primary"
                  secondaryTypographyProps={{ color: 'primary.main' }}
                  primary={
                    <>
                      {option.id === customer!.id ? t('current_prefix') : ''}
                      {option.name}
                    </>
                  }
                  secondary={
                    <>
                      <b>{t('created_at')}</b> {formatTimeAgo(option.createdAt)[0]}
                    </>
                  }
                />
              </ListItemButton>
            </ListItem>
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <LoadingButton
          variant="contained"
          disabled={!enableSwitch}
          type="submit"
          loading={switchCustomer.isPending}
        >
          {t('switch_customer')}
        </LoadingButton>
      </DialogActions>
    </Box>
  )
}

// @TODO: add error boundary
export const AdminSwitchCustomer = (props: AdminSwitchCustomerContentProps) => {
  return (
    <React.Suspense
      fallback={
        <DialogContent>
          <Skeleton variant="rectangular" width="100%" height={118} />
        </DialogContent>
      }
    >
      <AdminSwitchCustomerContent {...props} />
    </React.Suspense>
  )
}
